<template>
  <v-card class="pa-4" flat>
    <!-- مدال اتصال تنوع -->
    <custom-popup
      v-model="showLinkModal"
      :cons="{ title: 'اتصال تنوع به ووکامرس: ' + (selectedVariant ? selectedVariant.name : ''), icon: 'link-variant' }"
      max-width="800"
      hide-confirm
      @close="showLinkModal = false"
    >
      <link-woo-variant-modal
        v-if="showLinkModal"
        :variant-woo="selectedVariant"
        @saved="fetchVariants"
        @close="showLinkModal = false"
      />
    </custom-popup>

    <!-- نوار دکمه‌های بالا -->
    <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-4 gap-2">
      <!-- دکمه مدیریت قیمت گروهی در صورت انتخاب -->
      <v-slide-x-reverse-transition>
        <div v-if="selectedRows.length > 0" class="d-flex align-center">
          <v-chip class="ml-2 font-weight-bold" color="primary" outlined>
            {{ selectedRows.length }} تنوع انتخاب شده
          </v-chip>
          <v-btn
            class="btn-show rounded-lg px-4"
            elevation="2"
            @click="showBulkPriceModal = true"
          >
            <v-icon left>mdi-cash-edit</v-icon>
            مدیریت قیمت گروهی
          </v-btn>
        </div>
      </v-slide-x-reverse-transition>
    </div>

    <!-- جدول تنوع‌های غیرفعال ووکامرس -->
    <vue-table
      :data="variants"
      :headers="tableColumns"
      :loading.sync="loading"
      @change="onPagination"
      v-model="selectedRows"
      show-select
    >
      <!-- فیلترها -->
      <template v-slot:filters="{ props, perPage }">
        <v-row no-gutters class="mb-4">
          <v-col class="pa-2" cols="12" lg="2" md="3">
            <v-select
              v-model="props.length"
              :items="perPage"
              class="rounded-lg"
              dense
              hide-details
              label="تعداد در صفحه"
              outlined
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="4" md="4">
            <v-text-field
              v-model="props.search"
              label="جستجو (نام، SKU، شناسه WP)"
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              clearable
              hide-details
              class="rounded-lg"
              @keyup.enter="() => onPagination(props)"
              @click:clear="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-select
              v-model="props.is_linked"
              :items="linkOptions"
              label="وضعیت اتصال"
              dense
              hide-details
              outlined
              clearable
              class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-select
              v-model="props.type"
              :items="typeOptions"
              label="نوع کالا"
              dense
              hide-details
              outlined
              clearable
              class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="2" md="2">
            <v-select
              v-model="props.stock_status"
              :items="stockOptions"
              label="وضعیت موجودی"
              dense
              hide-details
              outlined
              clearable
              class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>
        </v-row>
      </template>

      <!-- ردیف -->
      <template v-slot:index="{ item }">
        {{ persian(item.index + 1 + ((variants?.current_page || 1) - 1) * (tableProps.length || 10)) }}
      </template>

      <!-- تصویر کالا -->
      <template v-slot:image="{ item }">
        <v-avatar size="115" rounded class="elevation-1">
          <v-img v-if="item.image" :src="item.image" contain />
          <v-icon v-else color="grey lighten-1">mdi-image-off-outline</v-icon>
        </v-avatar>
      </template>

      <!-- نوع کالا (ساده / متغیر) -->
      <template v-slot:type="{ item }">
        <v-chip
          small
          :color="item.type === 'variation' ? 'deep-purple lighten-5' : 'blue lighten-5'"
          :text-color="item.type === 'variation' ? 'deep-purple darken-3' : 'blue darken-3'"
          class="font-weight-bold"
        >
          <v-icon left x-small>{{ item.type === 'variation' ? 'mdi-sitemap' : 'mdi-cube-outline' }}</v-icon>
          {{ item.type === 'variation' ? 'دارای تنوع' : 'محصول ساده' }}
        </v-chip>
      </template>

      <!-- وضعیت فعال / غیرفعال -->
      <template v-slot:active="{ item }">
        <v-chip
          small
          :color="item.active ? 'success lighten-5' : 'grey lighten-3'"
          :text-color="item.active ? 'success darken-2' : 'grey darken-2'"
          class="font-weight-bold"
        >
          <v-icon left x-small>{{ item.active ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
          {{ item.active ? 'فعال' : 'غیرفعال' }}
        </v-chip>
      </template>

      <!-- نام تنوع و شناسه -->
      <template v-slot:name="{ item }">
        <div class="d-flex flex-column text-right">
          <a
            v-if="item.link"
            :href="item.link"
            target="_blank"
            class="text-decoration-none font-weight-bold primary--text mb-1 text-truncate"
            style="max-width: 320px;"
          >
            {{ item.name }}
            <v-icon x-small color="primary">mdi-open-in-new</v-icon>
          </a>
          <span v-else class="font-weight-bold mb-1 text-truncate" style="max-width: 320px;">
            {{ item.name }}
          </span>

          <div class="d-flex align-center grey--text text--darken-1 caption">
            <span class="ml-2">کد: {{ item.sku || '---' }}</span>
            <span>|</span>
            <span class="mr-2">WP ID: {{ persian(item.wp_id) }}</span>
          </div>
        </div>
      </template>

      <!-- ویژگی / رنگ -->
      <template v-slot:attribute="{ item }">
        <v-chip v-if="item.color" small outlined color="blue-grey" class="font-weight-medium">
          <v-icon left x-small>mdi-palette</v-icon>
          {{ item.color }}
        </v-chip>
        <span v-else class="grey--text">-</span>
      </template>

      <!-- قیمت عادی / فروش -->
      <template v-slot:price="{ item }">
        <div class="d-flex flex-column align-center">
          <div v-if="item.sale_price" class="text-decoration-line-through grey--text caption">
            {{ formatPrice(item.regular_price) }}
          </div>
          <span class="font-weight-bold" :class="item.sale_price ? 'red--text' : ''">
            {{ formatPrice(item.sale_price || item.regular_price) }} ریال
          </span>
        </div>
      </template>

      <!-- وضعیت موجودی -->
      <template v-slot:stock="{ item }">
        <v-chip
          small
          :color="item.stock_status === 'instock' ? 'green lighten-5' : 'red lighten-5'"
          :text-color="item.stock_status === 'instock' ? 'green darken-3' : 'red darken-3'"
          class="font-weight-bold"
        >
          <v-icon left x-small>{{ item.stock_status === 'instock' ? 'mdi-check' : 'mdi-close' }}</v-icon>
          {{ item.stock_status === 'instock' ? (item.stock_quantity ? `${persian(item.stock_quantity)} عدد` : 'موجود') : 'ناموجود' }}
        </v-chip>
      </template>

      <!-- وضعیت اتصال به دیجی‌کالا -->
      <template v-slot:is_linked="{ item }">
        <v-chip
          small
          :color="item.is_linked ? 'teal lighten-5' : 'orange lighten-5'"
          :text-color="item.is_linked ? 'teal darken-3' : 'orange darken-3'"
          class="font-weight-bold"
        >
          <v-icon left x-small>{{ item.is_linked ? 'mdi-link' : 'mdi-link-off' }}</v-icon>
          {{ item.is_linked ? 'متصل' : 'منفصل' }}
        </v-chip>
      </template>

      <!-- عملیات: فعال‌سازی مجدد، اتصال/قطع اتصال، ویرایش، آرشیو -->
      <template v-slot:action="{ item }">
        <action-buttons :data="item" :meta="metabutton(item)" />
      </template>
    </vue-table>
  </v-card>
</template>

<script>
import ActionButtons from "@/view/components/adapters/qrList/ordersListButton.vue";
import LinkWooVariantModal from "./LinkWooVariantModal.vue";

export default {
  name: "WooVariantsListDeactiveTab",
  components: {
    ActionButtons,
    LinkWooVariantModal,
  },
    props:{
    store_id:{type:Number, default:null}
  },

  data() {
    return {
      loading: false,
      variants: [],
      selectedVariant: null,
      showLinkModal: false,
      showBulkPriceModal: false,
      selectedRows: [],

      linkOptions: [
        { text: "همه", value: null },
        { text: "متصل شده", value: 1 },
        { text: "منفصل", value: 0 },
      ],
      stockOptions: [
        { text: "همه", value: null },
        { text: "موجود در انبار", value: "instock" },
        { text: "ناموجود", value: "outofstock" },
      ],
      typeOptions: [
        { text: "همه", value: null },
        { text: "متنوع (Variation)", value: "variation" },
        { text: "ساده (Simple)", value: "simple" },
      ],
      tableProps: {
        page: 1,
        length: 10,
        search: null,
        is_linked: null,
        type: null,
        stock_status: null,
        status: null,
        active: false,     
        is_archived: false,
        store_id:null,
      },
    };
  },

  computed: {
    tableColumns() {
      return [
        { value: "index", text: "ردیف", align: "center", sortable: false, width: "60px" },
        { value: "image", text: "تصویر", align: "center", sortable: false, width: "70px" },
        { value: "name", text: "نام و مشخصات", align: "start", sortable: true },
        { value: "type", text: "نوع", align: "center", sortable: false, width: "90px" },
        { value: "attribute", text: "رنگ / ویژگی", align: "center", sortable: false },
        { value: "price", text: "قیمت (ریال)", align: "center", sortable: true },
        { value: "stock", text: "وضعیت موجودی", align: "center", sortable: false },
        // { value: "active", text: "وضعیت", align: "center", sortable: false, width: "90px" },
        // { value: "is_linked", text: "اتصال به محصول", align: "center", sortable: false },
        { value: "action", text: "عملیات", align: "center", sortable: false, event: "actions" },
      ];
    },
  },

  mounted() {
    this.fetchVariants();
  },

  methods: {
    openWooShow(item) {
        const url = item.permalink;
        
        window.open(url, '_blank');
    },
    formatPrice(val) {
      if (!val) return "۰";
      return Number(val).toLocaleString("fa-IR");
    },

    persian(val) {
      if (val === null || val === undefined) return "";
      return val.toString().replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
    },

    metabutton(item) {
      const buttons = [
        {
          name: "translateShowWoo",
          label: "نمایش در فروشگاه",
          icon: "open-in-new", 
          class: "btn-show rounded-pill",
          action: (row) => this.openWooShow(row),
        },
        {
          name: "activate",
          label: "فعال‌سازی",
          icon: "check-circle-outline",
          class: "btn-edit rounded-pill",
          action: this.actions,
        },
        // {
        //   name: "edit",
        //   label: "ویرایش",
        //   icon: "pencil-outline",
        //   class: "btn-edit rounded-pill",
        //   action: this.actions,
        // },
      ];

      if (item.is_linked) {
        buttons.push({
          name: "unlink",
          label: "قطع اتصال",
          icon: "link-variant-off",
          class: "btn-delete rounded-pill",
          action: this.actions,
        });
      } 
        buttons.push({
          name: "link",
          label: "اتصال به محصول",
          icon: "link-variant-plus",
          class: "btn-show rounded-pill",
          action: this.actions,
        });
      

      buttons.push({
        name: "delete",
        label: "آرشیو",
        icon: "archive-outline",
        class: "btn-delete rounded-pill",
        action: this.actions,
      });

      return { buttons };
    },

    actions(item, type) {
      if (type === "activate") {
        this.toggleActiveVariant(item);
      } else if (type === "edit") {
        // this.selectedVariant = item;
        // باز کردن مدال ویرایش
      } else if (type === "link") {
        this.selectedVariant = item;
        this.showLinkModal = true;
      } else if (type === "unlink") {
        // this.unlinkVariant(item);
      } else if (type === "delete") {
        // this.archiveVariant(item);
      }
    },

    async toggleActiveVariant(item) {
      const confirm = await this.$modal.yesNo(
        `آیا از فعال‌سازی مجدد تنوع "${item.name}" اطمینان دارید؟`
      );
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.post(`/v1/woocommerce/variants/${item.id}/toggle-active`, {
          active: 1,
        });
        this.$toast.success("تنوع با موفقیت فعال شد.");
        this.fetchVariants();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در فعال‌سازی تنوع");
      } finally {
        this.loading = false;
      }
    },

    async fetchVariants() {
      this.loading = true;
      this.tableProps.store_id= this.store_id;
      const params = {};
      Object.keys(this.tableProps).forEach((key) => {
        if (this.tableProps[key] !== null && this.tableProps[key] !== "") {
          params[key] = this.tableProps[key];
        }
      });

      try {
        const { data } = await this.$api.get("/v1/woocommerce/variants", { params });
        this.variants = data.data || data;
      } catch (e) {
        this.$toast.error("خطا در دریافت لیست تنوع‌های غیرفعال");
      } finally {
        this.loading = false;
      }
    },

    async unlinkVariant(item) {
      const confirm = await this.$modal.yesNo(
        `آیا از قطع اتصال تنوع "${item.name}" از دیجی‌کالا اطمینان دارید؟`
      );
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.post(`v1/marketplace-listings/unlink-by-variant`,{  "marketplace": "woocommerce", 
          "external_variant_id": item.wp_id});
        this.$toast.success("اتصال تنوع با موفقیت قطع شد.");
        this.fetchVariants();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در قطع اتصال تنوع");
      } finally {
        this.loading = false;
      }
    },

    async archiveVariant(item) {
      const confirm = await this.$modal.yesNo(
        `آیا از انتقال تنوع "${item.name}" به آرشیو اطمینان دارید؟`
      );
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.delete(`/v1/woocommerce/variants/${item.id}`);
        this.$toast.success("تنوع با موفقیت به آرشیو منتقل شد.");
        this.fetchVariants();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در آرشیو تنوع");
      } finally {
        this.loading = false;
      }
    },

    onPagination(e) {
      if (e && typeof e === "object" && !Array.isArray(e)) {
        this.tableProps = { ...this.tableProps, ...e };
        if ("search" in e || "stock_status" in e || "type" in e || "is_linked" in e) {
          this.tableProps.page = 1;
        }
      }
      this.fetchVariants();
    },
  },
};
</script>
